import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, { defaultSuggestionsFilter, MentionData } from '@draft-js-plugins/mention';
import {
    EditorState,
    getDefaultKeyBinding,
    Modifier,
    RichUtils,
    ContentState,
    RawDraftContentState,
    convertToRaw,
    convertFromRaw,
} from 'draft-js';
import React, {
    CSSProperties,
    KeyboardEvent,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';

interface MentionInputProps {
    placeholder?: string;
    style?: CSSProperties;
    mentionData: MentionData[];
    onDone?(
        mentionedUserIds: number[],
        mentionedGroupIds: number[],
        rawContent: RawDraftContentState,
        text: string
    ): void | Promise<void>;
    onEscape?(): void | Promise<void>;
    autoFocus?: boolean;
    footer?: ReactNode;
    disabled?: boolean;
}

export function MentionInput({
    style,
    placeholder,
    mentionData,
    onDone,
    onEscape,
    autoFocus = false,
    footer,
    disabled = false,
}: MentionInputProps): ReactElement {
    const ref = useRef<Editor>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [open, setOpen] = useState(false);
    const [suggestions, setSuggestions] = useState(mentionData);

    useEffect(() => {
        if (autoFocus) {
            setTimeout(() => {
                ref.current?.focus();
            }, 10);
        }
    }, [autoFocus]);

    const { MentionSuggestions, plugins } = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            theme: {
                mention: 'mention',
                mentionSuggestionsPopup: 'mentionSuggestionsPopup',
                mentionSuggestions: 'mentionSuggestions',
                mentionSuggestionsEntryFocused: 'mentionSuggestionsEntryFocused',
                mentionSuggestionsEntry: 'mentionSuggestionsEntry',
            },
            mentionPrefix: '@',
            supportWhitespace: true,
        });
        const { MentionSuggestions } = mentionPlugin;
        const plugins = [mentionPlugin];

        return { plugins, MentionSuggestions };
    }, []);

    const onOpenChange = (_open: boolean) => {
        setOpen(_open);
    };

    const onSearchChange = useCallback(
        ({ value }: { value: string }) => {
            let filtered = mentionData;
            if (value !== '') {
                filtered = defaultSuggestionsFilter(value, mentionData);
            }
            setSuggestions(filtered);
        },
        [mentionData]
    );

    function removeSelectedBlocksStyle(editorState: EditorState) {
        const newContentState = RichUtils.tryToRemoveBlockStyle(editorState);
        if (newContentState) {
            return EditorState.push(editorState, newContentState, 'change-block-type');
        }
        return editorState;
    }

    function getResetEditorState(editorState: EditorState) {
        const blocks = editorState.getCurrentContent().getBlockMap().toList();
        const updatedSelection = editorState.getSelection().merge({
            anchorKey: blocks.first().get('key'),
            anchorOffset: 0,
            focusKey: blocks.last().get('key'),
            focusOffset: blocks.last().getLength(),
        });
        const newContentState = Modifier.removeRange(editorState.getCurrentContent(), updatedSelection, 'forward');

        const newState = EditorState.push(editorState, newContentState, 'remove-range');
        return removeSelectedBlocksStyle(newState);
    }

    function handleKeyCommand(command: string, editorState: EditorState) {
        if (command === 'new-line') {
            const currentContent = editorState.getCurrentContent();
            const selectionState = editorState.getSelection();
            const newContent = Modifier.insertText(currentContent, selectionState, '\n');
            const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
            setEditorState(newEditorState);

            return 'handled';
        }

        if (command === 'esc') {
            ref.current!.blur();
            onEscape?.();
            return 'handled';
        }

        return 'not-handled';
    }

    function bindKey(e: KeyboardEvent<Element>) {
        if (e.shiftKey && e.key === 'Enter') {
            return 'new-line';
        }

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            return undefined;
        }

        if (e.key === 'Escape') {
            return open ? undefined : 'esc';
        }

        if (e.key === 'Tab' && open) {
            return undefined;
        }

        return getDefaultKeyBinding(e);
    }

    function handleReturn(e: KeyboardEvent<Element>, editorState: EditorState): 'handled' | 'not-handled' {
        if (!open && !e.shiftKey) {
            const currentContent = editorState.getCurrentContent();
            const rawContent = convertToRaw(currentContent);
            const text = currentContent.getPlainText();
            const entities = rawContent.entityMap;

            const mentionedUserIds: number[] = [];
            const mentionedGroupIds: number[] = [];

            for (const key in entities) {
                if (Object.prototype.hasOwnProperty.call(entities, key)) {
                    const mentionData = entities[key].data as MentionData;
                    if (mentionData.mention.isGroup) {
                        mentionedGroupIds.push(mentionData.mention.itemId);
                    } else {
                        mentionedUserIds.push(mentionData.mention.itemId);
                    }
                }
            }

            onDone?.(mentionedUserIds, mentionedGroupIds, rawContent, text);
            const clearState = getResetEditorState(editorState);
            setEditorState(clearState);
            return 'handled';
        }

        return 'not-handled';
    }

    return (
        <div
            ref={containerRef}
            style={style}
            onClick={(e) => {
                if (containerRef.current?.isSameNode(e.target as Element)) {
                    ref.current!.focus();
                }
            }}
        >
            <Editor
                readOnly={disabled}
                placeholder={placeholder}
                editorState={editorState}
                onChange={setEditorState}
                handleKeyCommand={handleKeyCommand}
                handleReturn={handleReturn}
                keyBindingFn={bindKey}
                plugins={plugins}
                ref={ref}
            />
            {ReactDOM.createPortal(
                <MentionSuggestions
                    open={open}
                    onOpenChange={onOpenChange}
                    suggestions={suggestions}
                    onSearchChange={onSearchChange}
                />,
                document.getElementById('portal') as Element
            )}
            {!!footer && footer}
        </div>
    );
}

interface MentionsProps {
    style?: CSSProperties;
    rawContent: string;
    footer?: ReactNode;
}

function getEditorState(rawContent: string) {
    try {
        const draftContent = JSON.parse(rawContent) as RawDraftContentState;
        const content = convertFromRaw(draftContent);
        const editorState = EditorState.createWithContent(content);
        return editorState;
    } catch (error) {
        console.error(error);
        return EditorState.createWithContent(ContentState.createFromText('Content of message is corrupt...'));
    }
}

export function Mentions({ style, rawContent, footer }: MentionsProps) {
    const [editorState, setEditorState] = useState(() => getEditorState(rawContent));

    const plugins = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            theme: {
                mention: 'mention',
            },
            mentionPrefix: '@',
            supportWhitespace: true,
        });
        const plugins = [mentionPlugin];

        return plugins;
    }, []);

    return (
        <div style={style}>
            <Editor readOnly={true} onChange={setEditorState} editorState={editorState} plugins={plugins} />
            {!!footer && footer}
        </div>
    );
}
